<template>
  <div id="preloading">
    <div class="qr-loader-wrapper">
      <div class="qr-loader"></div>
      <div class="qr-loader-content">
        <h4>Hold on</h4>
        <p :class="{ 'hide-processing-payment-text': isPaymentMethodUpdated }">
          Thank you, your payment is being processed.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import AppConfig from '@/AppConfig';
import * as Sentry from '@sentry/vue';
import axios, { AxiosError } from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import type { Stripe } from '@stripe/stripe-js';
import type StripeService from '@/Services/Subscription/StripeService';

@Component
export default class Processing extends Vue {
  isReturningFromPaymentForm = this.$route.query.checkoutType || null;
  isPaymentMethodUpdated = this.$route.query.returningFromPaypal || null;
  isPaymentTypeWallet = this.$route.query.paymentType === 'wallet' || false;
  incompletePaymentMethodChangeApplied = false;
  userSubscription: any;
  defaultPaymentMethodReady = false;
  hasIncompleteSubscription: boolean | null = null;

  get stripeService(): StripeService {
    return this.$store.state.subscription.stripeService;
  }

  get stripe(): Stripe | null {
    return this.stripeService?.stripe || null;
  }

  async created(): Promise<void> {
    const selectedPlanId = this.$route.query.planId as string;

    // Check if returning from async payment with PaymentIntent (NEW Payment Elements flow)
    if (this.isReturningFromPaymentForm && this.isReturningFromAsyncPayment()) {
      await this.handleAsyncPaymentReturn(selectedPlanId);
      return;
    }

    if (this.isReturningFromPaymentForm && (this.isRedirectSuccessful() || this.isPaymentTypeWallet)) {
      await this.getUserSubscription(selectedPlanId);
      //check status of the subscription and depending on the status, redirect to the appropriate page
      if (!this.userSubscription || this.userSubscription.status !== 'active') {
        // Handle unsuccessful, processing, or canceled payments and API errors here
        await this.handlePaymentFailure();
      }
    }

    if (this.isRedirectSuccessful() && this.isPaymentMethodUpdated) {
      await this.updateChangedPaymentMethod();
      await this.waitForDefaultPaymentMethod();
    }

    if (this.isPaymentMethodUpdated) {
      await this.redirectToBilling();
    } else if (this.isReturningFromPaymentForm) {
      await this.redirectToPaymentComplete();
    } else {
      await this.$router.push({ path: '/features-and-pricing' });
    }
  }

  /**
   * Check if user is returning from async payment
   */
  isReturningFromAsyncPayment(): boolean {
    return !!(this.$route.query.payment_intent && this.$route.query.payment_intent_client_secret);
  }

  /**
   * Handle return from async payment provider
   * Verifies PaymentIntent status and polls for subscription activation
   */
  async handleAsyncPaymentReturn(selectedPlanId: string): Promise<void> {
    try {
      // Wait for Stripe to finish loading if it's still initializing
      if (!this.stripe && this.stripeService) {
        const stripeInstanceCountry = this.$store.state.auth.user?.stripe_instance_country;
        if (stripeInstanceCountry) {
          await this.stripeService.initStripe(stripeInstanceCountry);
        }
      }

      if (!this.stripe) {
        Sentry.captureException(new Error('Stripe not initialized'));
        await this.handlePaymentFailure();
        return;
      }

      const clientSecret = this.$route.query.payment_intent_client_secret as string;
      const { paymentIntent, error } = await this.stripe.retrievePaymentIntent(clientSecret);

      if (error) {
        Sentry.captureException(error);
        await this.handlePaymentFailure();
        return;
      }

      if (!paymentIntent) {
        Sentry.captureException(new Error('No PaymentIntent returned from Stripe'));
        await this.handlePaymentFailure();
        return;
      }

      // If payment intent is processing or succeeded, poll for subscription activation
      if (paymentIntent.status === 'processing' || paymentIntent.status === 'succeeded') {
        await this.waitForSubscriptionActivation(selectedPlanId);

        if (this.userSubscription && this.userSubscription.status === 'active') {
          await this.redirectToPaymentComplete();
        } else {
          // Timeout or payment failed
          Sentry.captureMessage('[Processing page] subscription activation timeout', {
            extra: {
              paymentIntentId: paymentIntent.id,
              planId: selectedPlanId,
              paymentIntentStatus: paymentIntent.status,
              subscriptionStatus: this.userSubscription?.status,
            },
          });
          await this.handlePaymentFailure();
        }
        return;
      }

      // Payment failed, canceled, or other terminal failure state
      await this.handlePaymentFailure();
    } catch (error) {
      Sentry.captureException(error);
      await this.handlePaymentFailure();
    }
  }

  /**
   * Handle payment failure by redirecting back to checkout
   */
  async handlePaymentFailure(): Promise<void> {
    await this.waitForIncompleteSubscriptionCleanup();

    const planName = this.$route.query.plan as string;
    if (planName) {
      const interval = this.$route.query.interval as string | undefined;
      await this.$router.push({
        name: 'checkout_payment_form_preselect_plan_name',
        params: { planName },
        ...(interval ? { query: { interval } } : {}),
      });
    } else {
      await this.$router.push({ path: '/features-and-pricing' });
    }
  }

  sleep = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  async performWithRetry(
    syncMethod: () => Promise<void>,
    condition: () => boolean,
    waitMultiplier: number
  ): Promise<void> {
    const retryAttempts: number = 5;
    let retry: number = 0;

    await syncMethod();

    do {
      if (retry > 0) {
        await this.sleep(retry * waitMultiplier);
        await syncMethod();
      }

      if (condition()) {
        break;
      }

      retry++;
    } while (retry < retryAttempts);
  }

  async updateChangedPaymentMethod(): Promise<void> {
    await this.performWithRetry(
      () => this.syncUpdateChangedPaymentMethod(),
      () => this.incompletePaymentMethodChangeApplied,
      1000
    );
  }

  async getUserSubscription(planId: string): Promise<void> {
    await this.performWithRetry(
      () => this.syncUserSubscription(planId),
      () => !this.userSubscription,
      500
    );
  }

  async waitForIncompleteSubscriptionCleanup(): Promise<void> {
    await this.performWithRetry(
      () => this.syncHasIncompleteSubscription(),
      () => this.hasIncompleteSubscription === false,
      2000
    );
  }

  async waitForSubscriptionActivation(planId: string): Promise<void> {
    // Poll for subscription to become active after PaymentIntent succeeds
    await this.performWithRetry(
      () => this.syncUserSubscription(planId),
      () => this.userSubscription?.status === 'active',
      2000
    );
  }

  async waitForDefaultPaymentMethod(): Promise<void> {
    await this.performWithRetry(
      () => this.syncDefaultPaymentMethod(),
      () => this.defaultPaymentMethodReady,
      1000
    );
  }

  async syncDefaultPaymentMethod(): Promise<void> {
    await axios
      .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods/default')
      .then(() => {
        this.defaultPaymentMethodReady = true;
      })
      .catch(() => {
        this.defaultPaymentMethodReady = false;
      });
  }

  async syncUserSubscription(planId: string): Promise<void> {
    await axios
      .get(AppConfig.getAPIBaseUrl() + `/subscription/stripe/${planId}`)
      .then(async (response) => {
        this.userSubscription = response.data;
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
      });
  }

  async syncHasIncompleteSubscription(): Promise<void> {
    await axios
      .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/has-incomplete')
      .then((response) => {
        this.hasIncompleteSubscription = response.data;
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
      });
  }

  async syncUpdateChangedPaymentMethod(): Promise<void> {
    if (!this.$route.query.setup_intent) {
      this.incompletePaymentMethodChangeApplied = true;
      Sentry.captureMessage('[Processing page] Missing setup_intent on PayPal payment-method return', {
        extra: { query: this.$route.query },
      });
      return;
    }

    await axios
      .post(AppConfig.getAPIBaseUrl() + '/team/payment-method-change/update', {
        setup_intent_id: this.$route.query.setup_intent,
      })
      .then(() => {
        this.incompletePaymentMethodChangeApplied = true;
      })
      .catch((error: AxiosError) => {
        this.incompletePaymentMethodChangeApplied = false;
        Sentry.captureException(error);
      });
  }

  isRedirectSuccessful(): boolean | string {
    return this.$route.query.redirect_status && this.$route.query.redirect_status === 'succeeded';
  }

  async redirectToBilling(): Promise<void> {
    await this.$router.push({ path: '/my-account/billing', query: this.$route.query });
  }

  async redirectToPaymentComplete(): Promise<void> {
    const routerQuery = { ...this.$route.query };
    delete routerQuery.checkoutType;

    await this.$router.push({
      path: `/features-and-pricing/complete/${this.$route.query.checkoutType}`,
      query: routerQuery,
    });
  }
}
</script>

<style lang="scss" scoped>
h4 {
  color: #1b294b;
  text-align: center;
  font-size: 26px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: -0.26px;
}
p {
  color: #53678c;
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.16px;
  white-space: nowrap;
}

.hide-processing-payment-text {
  visibility: hidden;
}

.qr-loader-wrapper {
  position: relative;
}
.qr-loader {
  margin: 0 auto;
}

.qr-loader-content {
  position: absolute;
  top: calc(100% + 12px);
  left: -103%;
}
</style>
